/**
 * 轨迹断段虚线桥：过久 / 过远 / 瞬移 / session 切开后，用虚线连接相邻段端点。
 */
import { analyzeTrackSegments } from './trackSegmentHelpers';
import { toMapLngLat } from './basemapCoord';

export const TRACK_GAP_DASHARRAY = [2, 2];
export const TRACK_GAP_OPACITY = 0.55;
export const TRACK_GAP_WIDTH_FACTOR = 0.85;

function displayCoord(point, map) {
  if (!point) return null;
  const lng = Number(point.longitude ?? point.lng ?? point.lon);
  const lat = Number(point.latitude ?? point.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (lng === 0 && lat === 0) return null;
  const coord = toMapLngLat(lng, lat, undefined, map) || [lng, lat];
  if (!coord || Number.isNaN(coord[0]) || Number.isNaN(coord[1])) return null;
  return coord;
}

/** @returns {Array<[number, number][]>} 每条桥两条坐标 */
export function buildTrackGapLineCoordinates(points = [], map = null) {
  const { gaps } = analyzeTrackSegments(points);
  return (gaps || [])
    .map((g) => {
      const a = displayCoord(g.from, map);
      const b = displayCoord(g.to, map);
      if (!a || !b) return null;
      return [a, b];
    })
    .filter(Boolean);
}

export function trackGapLayerId(baseLineId) {
  return `${baseLineId}-gap`;
}

export function removeTrackGapLayer(map, baseLineId) {
  if (!map || !baseLineId) return;
  const gapId = trackGapLayerId(baseLineId);
  try {
    if (map.getLayer?.(gapId)) map.removeLayer(gapId);
    if (map.getSource?.(gapId)) map.removeSource(gapId);
  } catch (_) {
    /* ignore */
  }
}

/**
 * 绘制/更新断段虚线层（与实线同色、略细、半透明、dash）。
 * @returns {string|null} gap layer id
 */
export function upsertTrackGapLayer(map, baseLineId, points, options = {}) {
  if (!map || !map.isStyleLoaded?.() || !baseLineId) return null;
  const gapCoords = buildTrackGapLineCoordinates(points, map);
  const gapId = trackGapLayerId(baseLineId);
  if (!gapCoords.length) {
    removeTrackGapLayer(map, baseLineId);
    return null;
  }
  const geojson = {
    type: 'Feature',
    properties: { kind: 'track-gap' },
    geometry:
      gapCoords.length === 1
        ? { type: 'LineString', coordinates: gapCoords[0] }
        : { type: 'MultiLineString', coordinates: gapCoords }
  };
  const lineColor = options.lineColor || '#ff0000';
  const lineWidth = Math.max(1.5, Number(options.lineWidth) || 3) * TRACK_GAP_WIDTH_FACTOR;
  try {
    const existing = map.getSource?.(gapId);
    if (existing && typeof existing.setData === 'function') {
      existing.setData(geojson);
      if (map.getLayer?.(gapId)) {
        try { map.setPaintProperty(gapId, 'line-color', lineColor); } catch (_) { /* ignore */ }
      }
      return gapId;
    }
    removeTrackGapLayer(map, baseLineId);
    map.addSource(gapId, { type: 'geojson', data: geojson });
    let beforeId = null;
    try {
      if (map.getLayer?.(baseLineId)) beforeId = baseLineId;
      else if (map.getLayer?.('member-markers')) beforeId = 'member-markers';
    } catch (_) { /* ignore */ }
    map.addLayer(
      {
        id: gapId,
        type: 'line',
        source: gapId,
        layout: { 'line-join': 'round', 'line-cap': 'butt' },
        paint: {
          'line-color': lineColor,
          'line-width': lineWidth,
          'line-opacity': TRACK_GAP_OPACITY,
          'line-dasharray': TRACK_GAP_DASHARRAY
        }
      },
      beforeId
    );
    return gapId;
  } catch (e) {
    console.warn('upsertTrackGapLayer failed:', e);
    return null;
  }
}
